import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { components } from './components';
// App.js
import { Backend, i18n, initReactI18next } from '@testsigma/atoms';

type updatePropsType = {
  updateProps: null | React.Dispatch<any>;
  updateMounted: null | React.Dispatch<any>;
};

export default class CustomElementWrapper extends HTMLElement {
  rendered = false;
  wrapper: HTMLElement | null = null;
  component: any;
  componentProps = {};

  stylesLoaded = [];
  root = null;

  constructor() {
    super();
  }

  init() {
    this.attachShadow({ mode: 'open' }); // Create a shadow root
    this.wrapper = document.createElement('div');
    this.wrapper.setAttribute('class', 'h-full w-full');
    this.shadowRoot?.append(this.wrapper);
    this.componentStyles.map((componentStyle) => this.attachStyle(`${componentStyle}-style`, this.shadowRoot));
    this.translationsInit();
  }

  translationsInit() {
    i18n
      // i18next-http-backend
      // loads translations from your server
      // https://github.com/i18next/i18next-http-backend
      .use(Backend)
      // detect user language
      // learn more: https://github.com/i18next/i18next-browser-languageDetector
      // .use(I18nextBrowserLanguageDetector)
      // pass the i18n instance to react-i18next.
      .use(initReactI18next)
      // init i18next
      // for all options read: https://www.i18next.com/overview/configuration-options
      .init({
        debug: true,
        fallbackLng: 'en-US',
        preload: ['en-US'],
        interpolation: {
          escapeValue: false, // not needed for react as it escapes by default
          // format: (value, format, lng) => { // legacy usage
          //   if (value instanceof Date) {
          //     return DateTime.fromJSDate(value).setLocale(lng).toLocaleString(DateTime[format])
          //   }
          //   return value;
          // }
        },
        backend: {
          loadPath: `${BASE_URL}locales/en-US/translation.json`,
          addPath: '/{{ns}}/locales/add/{{lng}}',
        },
      });
  }

  render() {
    if (!this.wrapper) return;

    const onLoad = (component: React.ComponentType) => {
      this.component = this.reactRender(component, this.wrapper as HTMLElement, this.componentProps);
    };

    // Loader
    this.wrapper.innerHTML = 'Loading...';
    this.importComponent?.().then((res) => onLoad(res.default));
  }

  reactRender(App: React.ComponentType<any>, el: HTMLElement, componentProps: object) {
    const ref: updatePropsType = { updateProps: null, updateMounted: null };
    const Wrapper: React.FC = () => {
      const [props, updateProps] = useState(componentProps);
      const [mounted, updateMounted] = useState(true);

      ref.updateProps = updateProps;
      ref.updateMounted = updateMounted;

      return (
        <React.StrictMode>
          <App props={props} mounted={mounted} />
        </React.StrictMode>
      );
    };

    this.root = ReactDOM.createRoot(el);
    this.root.render(
      <React.StrictMode>
        <Wrapper />
      </React.StrictMode>
    );

    return ref;
  }

  updateProps(props: object) {
    this.componentProps = props;
    if (this.component?.updateProps) this.component.updateProps(props);
  }

  connectedCallback() {
    if (!this.rendered) {
      this.init();
      this.rendered = true;
    }
  }

  attachStyle(componentStyle: string, target: HTMLElement | ShadowRoot, id = '') {
    const style = document.createElement('link');
    const loadedDetail = { loaded: false };

    if (id) {
      if (document.getElementById(id)) return;
      else style.id = id;
    }

    style.href = `${BASE_URL}custom-elements/${componentStyle}.css`; // TODO: handle domain
    style.rel = 'stylesheet';
    style.type = 'text/css';

    style.onload = () => {
      loadedDetail.loaded = true;

      if (this.stylesLoaded.every((item) => item.loaded)) this.render();
    };

    target?.append(style);
    this.stylesLoaded.push(loadedDetail);
  }

  disconnectedCallback() {
    this.component?.updateMounted(false);

    setTimeout(() => {
      this.root?.unmount();
      this.wrapper.remove();
      this.wrapper = null;
    });
  }

  get currentComponent() {
    return components.find(([component]) => component == this.componentName);
  }

  get componentName() {
    return this.tagName.toLowerCase().split('-').slice(1).join('-');
  }

  get importComponent() {
    return this.currentComponent?.[1];
  }

  get componentStyles(): Array<string> {
    return ['audit-trail', ...((this.currentComponent?.[2] as Array<string>) || [])];
  }
}
